import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearCurrentUser } from "../redux/reducers/userSlice";
import { baseApi } from "../redux/apiMananger";

const useCurrentUser = () => {
  const user = useSelector((state) => state.user.currentUser);
  return user;
};

/**
 * Hook personnalisé pour gérer l'authentification
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  /**
   * Récupère le token d'accès
   */
  const getToken = () => {
    if (!currentUser) return null;
    
    // Support de plusieurs formats:
    // 1. { token: "eyJ..." } - String directe
    // 2. { access_token: "eyJ..." } - JWT standard
    // 3. { token: { token: "eyJ..." } } - Objet imbriqué
    
    if (typeof currentUser.token === 'string') {
      return currentUser.token;
    }
    if (currentUser.access_token) {
      return currentUser.access_token;
    }
    if (currentUser.token?.token) {
      return currentUser.token.token;
    }
    
    return null;
  };

  /**
   * Vérifie si l'utilisateur est authentifié
   */
  const isAuthenticated = () => {
    return !!currentUser && !!getToken();
  };

  /**
   * Déconnecte l'utilisateur
   */
  const logout = () => {
    // Vider le cache RTK Query (données API)
    dispatch(baseApi.util.resetApiState());

    // Nettoyer le state Redux
    dispatch(clearCurrentUser());
    
    // Nettoyer localStorage (optionnel, redux-persist le fait déjà)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    
    // Rediriger vers login
    navigate('/login', { replace: true });
  };

  /**
   * Vérifie si l'utilisateur a un rôle spécifique
   */
  const hasRole = (role) => {
    return currentUser?.status === role;
  };

  /**
   * Vérifie si l'utilisateur est admin
   */
  const isAdmin = () => hasRole('ADMIN');

  /**
   * Vérifie si l'utilisateur est partenaire
   */
  const isPartenaire = () => hasRole('PARTENAIRE');

  return {
    currentUser,
    isAuthenticated,
    logout,
    getToken,
    hasRole,
    isAdmin,
    isPartenaire,
  };
};

export default useCurrentUser;
